<template>
  <div>
    <b-col md="12">
      <div>
        <b-modal
          :id="`modal-confirm-print`"
          :no-close-on-backdrop="true"
          :title="$t('surgery.print-invoice')"
          ok-only
          :ok-title="$t('common.print')"
          @ok="printInvoice"
        >
          <b-card-text>
            <h5>{{ $t("surgery.print-invoice") }}</h5>
            {{ $t("surgery.print-sure") }}
          </b-card-text>
        </b-modal>

        <!-- add surgery invoice modal -->
        <b-modal
          id="add-surgery-invoice-modal"
          :no-close-on-backdrop="true"
          :title="
            isEditing
              ? $t('hospital.update-surgery-invoice')
              : $t('hospital.create-surgery-invoice')
          "
          ok-only
          size="lg"
          :ok-title="isEditing ? $t('common.update') : $t('common.add')"
        >
          <template #modal-footer="{}">
            <b-button
              ref="button"
              variant="primary"
              class="m-0"
              :disabled="loading"
              block
              @click="submitItem"
            >
              <span v-if="!loading">{{
                isEditing ? $t("common.update") : $t("common.add")
              }}</span>
              <b-spinner
                v-else
                small
              />
            </b-button>
          </template>
          <b-card-text>
            <b-alert
              v-if="!isEditing && insuranceSurgeryList.length === 0"
              variant="warning"
              show
            >
              <div class="alert-body">
                <span>{{ $t('surgery.no-available-insurance-surgeries') }}</span>
              </div>
            </b-alert>
            <b-row>
              <b-col v-if="!isEditing">
                <b-form-group
                  :label="$t('surgery.insurance-deposit-invoice')"
                  class="required"
                >
                  <v-select
                    v-model="EditForm.invoice_insurance_surgery_id"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :clearable="false"
                    label="title"
                    :options="insuranceSurgeryList"
                    :reduce="(option) => option.value"
                    :disabled="insuranceSurgeryList.length === 0"
                    @input="onInsuranceSurgeryChange"
                  />
                  <p class="text-danger mt-1">
                    {{ errorForm.invoice_insurance_surgery_id }}
                  </p>
                </b-form-group>
              </b-col>
              <b-col
                v-else
                cols="12"
              >
                <b-alert
                  variant="info"
                  show
                  class="mb-3"
                >
                  <div class="d-flex align-items-center">
                    <i class="fas fa-info-circle mr-2" />
                    <span>{{ $t('surgery.editing-invoice-amounts') }}</span>
                  </div>
                </b-alert>
              </b-col>
              <b-col lg="4">
                <b-form-group
                  :label="$t('surgery.room_amount')"
                  label-for="room_amount"
                  class="required"
                >
                  <b-form-input
                    id="room_amount"
                    v-model="EditForm.room_amount"
                    type="number"
                    :placeholder="$t('surgery.room_amount')"
                  />
                  <p class="text-danger mt-1">
                    {{ errorForm.room_amount }}
                  </p>
                </b-form-group>
              </b-col>

              <b-col lg="4">
                <b-form-group
                  :label="$t('surgery.admin_services_amount')"
                  label-for="admin_services_amount"
                  class="required"
                >
                  <b-form-input
                    id="admin_services_amount"
                    v-model="EditForm.admin_services_amount"
                    type="number"
                    :placeholder="$t('surgery.admin_services_amount')"
                  />
                  <p class="text-danger mt-1">
                    {{ errorForm.admin_services_amount }}
                  </p>
                </b-form-group>
              </b-col>

              <b-col lg="4">
                <b-form-group
                  :label="$t('surgery.general_services_amount')"
                  label-for="general_services_amount"
                  class="required"
                >
                  <b-form-input
                    id="general_services_amount"
                    v-model="EditForm.general_services_amount"
                    type="number"
                    :placeholder="$t('surgery.general_services_amount')"
                  />
                  <p class="text-danger mt-1">
                    {{ errorForm.general_services_amount }}
                  </p>
                </b-form-group>
              </b-col>

              <b-col lg="4">
                <b-form-group
                  :label="$t('surgery.nursing_amount')"
                  label-for="nursing_amount"
                  class="required"
                >
                  <b-form-input
                    id="nursing_amount"
                    v-model="EditForm.nursing_amount"
                    type="number"
                    :placeholder="$t('surgery.nursing_amount')"
                  />
                  <p class="text-danger mt-1">
                    {{ errorForm.nursing_amount }}
                  </p>
                </b-form-group>
              </b-col>

              <b-col lg="4">
                <b-form-group
                  :label="$t('surgery.surgery_room_amount')"
                  label-for="surgery_room_amount"
                  class="required"
                >
                  <b-form-input
                    id="surgery_room_amount"
                    v-model="EditForm.surgery_room_amount"
                    type="number"
                    :placeholder="$t('surgery.surgery_room_amount')"
                  />
                  <p class="text-danger mt-1">
                    {{ errorForm.surgery_room_amount }}
                  </p>
                </b-form-group>
              </b-col>

              <b-col lg="4">
                <b-form-group
                  :label="$t('surgery.labour_room_amount')"
                  label-for="labour_room_amount"
                  class="required"
                >
                  <b-form-input
                    id="labour_room_amount"
                    v-model="EditForm.labour_room_amount"
                    type="number"
                    :placeholder="$t('surgery.labour_room_amount')"
                    class="required"
                  />
                  <p class="text-danger mt-1">
                    {{ errorForm.labour_room_amount }}
                  </p>
                </b-form-group>
              </b-col>

              <b-col lg="4">
                <b-form-group
                  :label="$t('surgery.medical_consumables_amount')"
                  label-for="medical_consumables_amount"
                  class="required"
                >
                  <b-form-input
                    id="medical_consumables_amount"
                    v-model="EditForm.medical_consumables_amount"
                    type="number"
                    :placeholder="$t('surgery.medical_consumables_amount')"
                  />
                  <p class="text-danger mt-1">
                    {{ errorForm.medical_consumables_amount }}
                  </p>
                </b-form-group>
              </b-col>

              <b-col lg="4">
                <b-form-group
                  :label="$t('surgery.pre_incubator_amount')"
                  label-for="pre_incubator_amount"
                  class="required"
                >
                  <b-form-input
                    id="pre_incubator_amount"
                    v-model="EditForm.pre_incubator_amount"
                    type="number"
                    :placeholder="$t('surgery.pre_incubator_amount')"
                  />
                  <p class="text-danger mt-1">
                    {{ errorForm.pre_incubator_amount }}
                  </p>
                </b-form-group>
              </b-col>

              <b-col lg="6">
                <b-form-group
                  :label="$t('surgery.pre_cpap_amount')"
                  label-for="pre_cpap_amount"
                  class="required"
                >
                  <b-form-input
                    id="pre_cpap_amount"
                    v-model="EditForm.pre_cpap_amount"
                    type="number"
                    :placeholder="$t('surgery.pre_cpap_amount')"
                  />
                  <p class="text-danger mt-1">
                    {{ errorForm.pre_cpap_amount }}
                  </p>
                </b-form-group>
              </b-col>

              <b-col lg="6">
                <b-form-group
                  :label="$t('surgery.scanner_device_amount')"
                  label-for="scanner_device_amount"
                  class="required"
                >
                  <b-form-input
                    id="scanner_device_amount"
                    v-model="EditForm.scanner_device_amount"
                    type="number"
                    :placeholder="$t('surgery.scanner_device_amount')"
                  />
                  <p class="text-danger mt-1">
                    {{ errorForm.scanner_device_amount }}
                  </p>
                </b-form-group>
              </b-col>
              <b-col lg="12">
                <hr style="border-top: 1px solid #000; margin: 20px 0">
              </b-col>
              <b-col lg="4">
                <b-form-group
                  :label="$t('surgery.surgeon_amount')"
                  label-for="surgeon_amount"
                  class="required"
                >
                  <b-form-input
                    id="surgeon_amount"
                    v-model="EditForm.surgeon_amount"
                    type="number"
                    :placeholder="$t('surgery.surgeon_amount')"
                  />
                  <p class="text-danger mt-1">
                    {{ errorForm.surgeon_amount }}
                  </p>
                </b-form-group>
              </b-col>

              <b-col lg="4">
                <b-form-group
                  :label="$t('surgery.surgery_nurse_assist_amount')"
                  label-for="surgery_nurse_assist_amount"
                  class="required"
                >
                  <b-form-input
                    id="surgery_nurse_assist_amount"
                    v-model="EditForm.surgery_nurse_assist_amount"
                    type="number"
                    :placeholder="$t('surgery.surgery_nurse_assist_amount')"
                  />
                  <p class="text-danger mt-1">
                    {{ errorForm.surgery_nurse_assist_amount }}
                  </p>
                </b-form-group>
              </b-col>

              <b-col lg="4">
                <b-form-group
                  :label="$t('surgery.surgeon_assist_amount')"
                  label-for="surgeon_assist_amount"
                  class="required"
                >
                  <b-form-input
                    id="surgeon_assist_amount"
                    v-model="EditForm.surgeon_assist_amount"
                    type="number"
                    :placeholder="$t('surgery.surgeon_assist_amount')"
                  />
                  <p class="text-danger mt-1">
                    {{ errorForm.surgeon_assist_amount }}
                  </p>
                </b-form-group>
              </b-col>

              <b-col lg="4">
                <b-form-group
                  :label="$t('surgery.anesthetist_amount')"
                  label-for="anesthetist_amount"
                  class="required"
                >
                  <b-form-input
                    id="anesthetist_amount"
                    v-model="EditForm.anesthetist_amount"
                    type="number"
                    :placeholder="$t('surgery.anesthetist_amount')"
                  />
                  <p class="text-danger mt-1">
                    {{ errorForm.anesthetist_amount }}
                  </p>
                </b-form-group>
              </b-col>

              <b-col lg="4">
                <b-form-group
                  :label="$t('surgery.anaesthetist_assist_amount')"
                  label-for="anaesthetist_assist_amount"
                  class="required"
                >
                  <b-form-input
                    id="anaesthetist_assist_amount"
                    v-model="EditForm.anaesthetist_assist_amount"
                    type="number"
                    :placeholder="$t('surgery.anaesthetist_assist_amount')"
                  />
                  <p class="text-danger mt-1">
                    {{ errorForm.anaesthetist_assist_amount }}
                  </p>
                </b-form-group>
              </b-col>

              <b-col lg="4">
                <b-form-group
                  :label="$t('surgery.pre_test_amount')"
                  label-for="pre_test_amount"
                  class="required"
                >
                  <b-form-input
                    id="pre_test_amount"
                    v-model="EditForm.pre_test_amount"
                    type="number"
                    :placeholder="$t('surgery.pre_test_amount')"
                  />
                  <p class="text-danger mt-1">
                    {{ errorForm.pre_test_amount }}
                  </p>
                </b-form-group>
              </b-col>

              <b-col lg="4">
                <b-form-group
                  :label="$t('surgery.tissue_transplant_amount')"
                  label-for="tissue_transplant_amount"
                  class="required"
                >
                  <b-form-input
                    id="tissue_transplant_amount"
                    v-model="EditForm.tissue_transplant_amount"
                    type="number"
                    :placeholder="$t('surgery.tissue_transplant_amount')"
                  />
                  <p class="text-danger mt-1">
                    {{ errorForm.tissue_transplant_amount }}
                  </p>
                </b-form-group>
              </b-col>

              <b-col lg="4">
                <b-form-group
                  :label="$t('surgery.op_nurse_amount')"
                  label-for="op_nurse_amount"
                  class="required"
                >
                  <b-form-input
                    id="op_nurse_amount"
                    v-model="EditForm.op_nurse_amount"
                    type="number"
                    :placeholder="$t('surgery.op_nurse_amount')"
                    class="required"
                  />
                  <p class="text-danger mt-1">
                    {{ errorForm.op_nurse_amount }}
                  </p>
                </b-form-group>
              </b-col>

              <b-col lg="4">
                <b-form-group
                  :label="$t('surgery.consultation_amount')"
                  label-for="consultation_amount"
                  class="required"
                >
                  <b-form-input
                    id="consultation_amount"
                    v-model="EditForm.consultation_amount"
                    type="number"
                    :placeholder="$t('surgery.consultation_amount')"
                  />
                  <p class="text-danger mt-1">
                    {{ errorForm.consultation_amount }}
                  </p>
                </b-form-group>
              </b-col>
              <b-col lg="12">
                <b-form-group
                  :label="$t('common.date')"
                  label-for="date"
                  class="required"
                >
                  <b-form-input
                    id="date"
                    v-model="date"
                    :disabled="true"
                    type="datetime"
                    :placeholder="$t('common.date')"
                  />
                </b-form-group>
              </b-col>
            </b-row>
          </b-card-text>
        </b-modal>
        <b-card>
          <!-- <b-row>
            <b-col md="4" cols="6">
              <b-form-group :label="$t('common.from-date')" label-for="date">
                <b-form-datepicker
                  id="example-datepicker"
                  v-model="dateFrom"
                  class="mb-2"
                  today-button
                  reset-button
                  close-button
                ></b-form-datepicker>
              </b-form-group>
            </b-col>
            <b-col md="4" cols="6">
              <b-form-group :label="$t('common.to-date')" label-for="date">
                <b-form-datepicker
                  id="example-datepicker2"
                  v-model="dateTo"
                  class="mb-2"
                  today-button
                  reset-button
                  close-button
                ></b-form-datepicker>
              </b-form-group>
            </b-col>
          </b-row> -->

          <!-- <b-row class="mb-2 mt-1">
            <b-col md="4" cols="6">
              <b-form-group
                :label="$t('common.hospital-net-amount-sum')"
                label-for="name"
              >
                <h3>
                  <b-badge variant="primary">{{
                    formatIQDCurrency(sums.hospital_net_amount_sum)
                  }}</b-badge>
                </h3>
              </b-form-group>
            </b-col>
            <b-col md="4" cols="6">
              <b-form-group
                :label="$t('common.total-amount-sum')"
                label-for="name"
              >
                <h3>
                  <b-badge variant="primary">{{
                    formatIQDCurrency(sums.total_amount_sum)
                  }}</b-badge>
                </h3>
              </b-form-group>
            </b-col>
            <b-col md="4" cols="12">
              <b-button
                class="float-right"
                variant="primary"
                @click="getPatientSurgeriesInvoices()"
              >
                {{ $t("common.search") }}
              </b-button>
            </b-col>
            <b-col md="4" cols="6">
              <p class="text-muted font-weight-bold">
                {{ $t("common.this-sums-based-on-the-selected-date") }}
              </p>
            </b-col>
          </b-row> -->

          <div>
            <!-- Data Table  -->
            <div class="d-flex justify-content-between mt-4 mb-2">
              <h2>{{ $t("common.invoices") }}</h2>
              <b-button
                v-if="$route.query.patientCardId && splitDummyData1.length > 0"
                variant="primary"
                class="p-1 mb-1"
                @click="openAddModal()"
              >
                <feather-icon
                  icon="PlusIcon"
                  size="16"
                  class="mr-1"
                />
                {{ $t("hospital.create-surgery-invoice") }}
              </b-button>
            </div>

            <!-- Empty State -->
            <div
              v-if="!loading && splitDummyData1.length === 0"
              class="text-center py-5"
            >
              <feather-icon
                icon="FileTextIcon"
                size="64"
                class="text-muted mb-3"
              />
              <h4 class="text-muted">
                {{ $t('common.no-invoices-found') }}
              </h4>
              <p class="text-muted">
                {{ $t('surgery.no-surgery-invoices-message') }}
              </p>
              <b-button
                v-if="$route.query.patientCardId"
                variant="primary"
                class="mt-2"
                @click="openAddModal()"
              >
                <feather-icon
                  icon="PlusIcon"
                  size="16"
                  class="mr-1"
                />
                {{ $t("hospital.create-surgery-invoice") }}
              </b-button>
            </div>

            <!-- Accordion Style Invoice List -->
            <div
              v-if="!loading && splitDummyData1.length > 0"
              class="accordion"
              role="tablist"
            >
              <div
                v-for="(invoice, index) in splitDummyData1"
                :key="invoice.id"
                class="mb-2"
              >
                <b-badge
                  variant="light"
                  class="w-100 cursor-pointer p-0"
                  :class="{ 'badge-expanded': expandedInvoiceId === invoice.id }"
                  style="display: block;"
                  @click="toggleCollapse(invoice.id)"
                >
                  <div
                    class="d-flex justify-content-between align-items-center p-2"
                    role="tab"
                  >
                    <div
                      class="flex-grow-1 cursor-pointer d-flex align-items-center"
                    >
                      <!-- Chevron Icon -->
                      <div class="mr-2">
                        <feather-icon
                          icon="ChevronRightIcon"
                          size="30"
                          class="collapse-icon"
                          :class="{ 'rotate-90': expandedInvoiceId === invoice.id }"
                        />
                      </div>

                      <div class="d-flex align-items-center flex-wrap">
                        <b-badge
                          variant="primary"
                          class="mr-2"
                          style="font-size: 1rem; padding: 0.5rem 0.75rem;"
                        >
                          #{{ invoice.invoice_no }}
                        </b-badge>

                        <b-badge
                          variant="success"
                          class="mr-2"
                          style="font-size: 1rem; padding: 0.5rem 0.75rem;"
                        >
                          {{ formatIQDCurrency(invoice.total_amount) }}
                        </b-badge>

                        <b-badge
                          v-if="invoice.discount > 0"
                          variant="warning"
                          class="mr-2"
                          style="font-size: 1rem; padding: 0.5rem 0.75rem;"
                        >
                          {{ $t("accounting.discount") }}: {{ formatIQDCurrency(invoice.discount) }}
                        </b-badge>

                        <small class="text-muted ml-2">
                          <feather-icon
                            icon="InfoIcon"
                            size="14"
                            class="mr-1"
                          />
                          {{ $t("common.click-to-expand") }}
                        </small>
                      </div>
                    </div>
                    <div class="d-flex align-items-center">
                      <div class="mr-2">
                        <ChangeInvoiceStatusDropDown
                          name="surgeryInvoice"
                          endpoint="invoicePatientSergury"
                          :data="invoice"
                          @onEmit="$emit('onChangeStatus')"
                        />
                      </div>
                      <b-button
                        v-if="
                          $store.state.app.permissions.includes(
                            'apply-invoice-discount'
                          ) &&
                            invoice.status !== 'printed' &&
                            invoice.status !== 'approved'
                        "
                        variant="outline-warning"
                        size="sm"
                        class="mr-2"
                        @click="showDiscountModal(invoice)"
                      >
                        <feather-icon
                          icon="PercentIcon"
                          size="14"
                          class="mr-1"
                        />
                        {{ $t("common.apply-discount") }}
                      </b-button>
                      <b-button
                        v-if="invoice.status === 'approved'"
                        variant="outline-primary"
                        size="sm"
                        class="mr-2"
                        @click="showModal(invoice)"
                      >
                        {{ $t("reception.Print Invoice") }}
                      </b-button>
                      <b-button
                        variant="outline-info"
                        size="sm"
                        @click="editInvoice(invoice)"
                      >
                        <feather-icon
                          icon="EditIcon"
                          size="14"
                        />
                      </b-button>
                    </div>
                  </div>
                </b-badge>

                <b-collapse
                  :id="`accordion-${invoice.id}`"
                  accordion="surgery-invoices-accordion"
                  role="tabpanel"
                  @show="expandedInvoiceId = invoice.id"
                  @hide="expandedInvoiceId = null"
                >
                  <div class="collapse-content">
                    <b-card-body>
                      <b-row>
                        <b-col
                          lg="12"
                          class="mb-3"
                        >
                          <h5 class="text-primary">
                            {{ $t("surgery.hospital-services") }}
                          </h5>
                          <hr>
                        </b-col>

                        <b-col
                          lg="4"
                          md="6"
                          class="mb-2"
                        >
                          <strong>{{ $t("surgery.room_amount") }}:</strong>
                          <span class="ml-2">{{
                            formatIQDCurrency(invoice.room_amount)
                          }}</span>
                        </b-col>

                        <b-col
                          lg="4"
                          md="6"
                          class="mb-2"
                        >
                          <strong>{{ $t("surgery.admin_services_amount") }}:</strong>
                          <span class="ml-2">{{
                            formatIQDCurrency(invoice.admin_services_amount)
                          }}</span>
                        </b-col>

                        <b-col
                          lg="4"
                          md="6"
                          class="mb-2"
                        >
                          <strong>{{ $t("surgery.general_services_amount") }}:</strong>
                          <span class="ml-2">{{
                            formatIQDCurrency(invoice.general_services_amount)
                          }}</span>
                        </b-col>

                        <b-col
                          lg="4"
                          md="6"
                          class="mb-2"
                        >
                          <strong>{{ $t("surgery.nursing_amount") }}:</strong>
                          <span class="ml-2">{{
                            formatIQDCurrency(invoice.nursing_amount)
                          }}</span>
                        </b-col>

                        <b-col
                          lg="4"
                          md="6"
                          class="mb-2"
                        >
                          <strong>{{ $t("surgery.surgery_room_amount") }}:</strong>
                          <span class="ml-2">{{
                            formatIQDCurrency(invoice.surgery_room_amount)
                          }}</span>
                        </b-col>

                        <b-col
                          lg="4"
                          md="6"
                          class="mb-2"
                        >
                          <strong>{{ $t("surgery.labour_room_amount") }}:</strong>
                          <span class="ml-2">{{
                            formatIQDCurrency(invoice.labour_room_amount)
                          }}</span>
                        </b-col>

                        <b-col
                          lg="4"
                          md="6"
                          class="mb-2"
                        >
                          <strong>{{
                            $t("surgery.medical_consumables_amount")
                          }}:</strong>
                          <span class="ml-2">{{
                            formatIQDCurrency(invoice.medical_consumables_amount)
                          }}</span>
                        </b-col>

                        <b-col
                          lg="4"
                          md="6"
                          class="mb-2"
                        >
                          <strong>{{ $t("surgery.pre_incubator_amount") }}:</strong>
                          <span class="ml-2">{{
                            formatIQDCurrency(invoice.pre_incubator_amount)
                          }}</span>
                        </b-col>

                        <b-col
                          lg="4"
                          md="6"
                          class="mb-2"
                        >
                          <strong>{{ $t("surgery.pre_cpap_amount") }}:</strong>
                          <span class="ml-2">{{
                            formatIQDCurrency(invoice.pre_cpap_amount)
                          }}</span>
                        </b-col>

                        <b-col
                          lg="4"
                          md="6"
                          class="mb-2"
                        >
                          <strong>{{ $t("surgery.scanner_device_amount") }}:</strong>
                          <span class="ml-2">{{
                            formatIQDCurrency(invoice.scanner_device_amount)
                          }}</span>
                        </b-col>

                        <b-col
                          lg="12"
                          class="mt-3 mb-3"
                        >
                          <h5 class="text-primary">
                            {{ $t("surgery.doctor-services") }}
                          </h5>
                          <hr>
                        </b-col>

                        <b-col
                          lg="4"
                          md="6"
                          class="mb-2"
                        >
                          <strong>{{ $t("surgery.surgeon_amount") }}:</strong>
                          <span class="ml-2">{{
                            formatIQDCurrency(invoice.surgeon_amount)
                          }}</span>
                        </b-col>

                        <b-col
                          lg="4"
                          md="6"
                          class="mb-2"
                        >
                          <strong>{{
                            $t("surgery.surgery_nurse_assist_amount")
                          }}:</strong>
                          <span class="ml-2">{{
                            formatIQDCurrency(invoice.surgery_nurse_assist_amount)
                          }}</span>
                        </b-col>

                        <b-col
                          lg="4"
                          md="6"
                          class="mb-2"
                        >
                          <strong>{{ $t("surgery.surgeon_assist_amount") }}:</strong>
                          <span class="ml-2">{{
                            formatIQDCurrency(invoice.surgeon_assist_amount)
                          }}</span>
                        </b-col>

                        <b-col
                          lg="4"
                          md="6"
                          class="mb-2"
                        >
                          <strong>{{ $t("surgery.anesthetist_amount") }}:</strong>
                          <span class="ml-2">{{
                            formatIQDCurrency(invoice.anesthetist_amount)
                          }}</span>
                        </b-col>

                        <b-col
                          lg="4"
                          md="6"
                          class="mb-2"
                        >
                          <strong>{{
                            $t("surgery.anaesthetist_assist_amount")
                          }}:</strong>
                          <span class="ml-2">{{
                            formatIQDCurrency(invoice.anaesthetist_assist_amount)
                          }}</span>
                        </b-col>

                        <b-col
                          lg="4"
                          md="6"
                          class="mb-2"
                        >
                          <strong>{{ $t("surgery.pre_test_amount") }}:</strong>
                          <span class="ml-2">{{
                            formatIQDCurrency(invoice.pre_test_amount)
                          }}</span>
                        </b-col>

                        <b-col
                          lg="4"
                          md="6"
                          class="mb-2"
                        >
                          <strong>{{ $t("surgery.tissue_transplant_amount") }}:</strong>
                          <span class="ml-2">{{
                            formatIQDCurrency(invoice.tissue_transplant_amount)
                          }}</span>
                        </b-col>

                        <b-col
                          lg="4"
                          md="6"
                          class="mb-2"
                        >
                          <strong>{{ $t("surgery.op_nurse_amount") }}:</strong>
                          <span class="ml-2">{{
                            formatIQDCurrency(invoice.op_nurse_amount)
                          }}</span>
                        </b-col>

                        <b-col
                          lg="4"
                          md="6"
                          class="mb-2"
                        >
                          <strong>{{ $t("surgery.consultation_amount") }}:</strong>
                          <span class="ml-2">{{
                            formatIQDCurrency(invoice.consultation_amount)
                          }}</span>
                        </b-col>

                        <b-col
                          lg="12"
                          class="mt-3"
                        >
                          <hr>
                          <div
                            class="d-flex justify-content-between align-items-center"
                          >
                            <div>
                              <strong
                                class="text-muted"
                              >{{ $t("common.date") }}:</strong>
                              <span class="ml-2">{{ invoice.date }}</span>
                            </div>
                            <div>
                              <strong
                                class="text-primary"
                              >{{ $t("surgery.hospital_net_amount") }}:</strong>
                              <span class="ml-2 font-weight-bolder">{{
                                formatIQDCurrency(invoice.hospital_net_amount)
                              }}</span>
                            </div>
                          </div>
                        </b-col>
                      </b-row>
                    </b-card-body>
                  </div>
                </b-collapse>
              </div>
            </div>
            <template v-if="loading">
              <div>
                <div class="d-flex justify-content-center mb-1">
                  <b-spinner label="Loading..." />
                </div>
              </div>
            </template>

            <!-- Total invoices count -->
            <div
              v-if="!loading && splitDummyData1.length > 0"
              class="mx-2 mb-2 mt-3"
            >
              <b-row>
                <b-col
                  cols="12"
                  class="text-center"
                >
                  <b-badge
                    variant="secondary"
                    style="font-size: 0.9rem; padding: 0.5rem 1rem;"
                  >
                    {{ $t("common.total") }}: {{ splitDummyData1.length }} {{ $t("common.invoices") }}
                  </b-badge>
                </b-col>
              </b-row>
            </div>
          </div>
        </b-card>
      </div>

      <!-- Hidden print button for ServicesInvoice -->
      <b-button
        ref="surgeryInvoiceRef"
        v-print="ServicesInvoiceBtn"
        variant="outline-primary"
        size="sm"
        class="hidden"
      >
        {{ $t("reception.Print Invoice") }}
      </b-button>

      <ServicesInvoice
        :patient-data="patientData"
        :data="invoice"
      />

      <!-- Modal to apply discount -->
      <b-modal
        id="modal-apply-discount"
        centered
        size="md"
        :title="$t('common.apply-discount')"
        @ok="applyDiscount"
      >
        <b-card-text>
          <b-form-group
            :label="$t('common.invoice-number')"
            label-for="discount-invoice-no"
          >
            <b-form-input
              id="discount-invoice-no"
              :value="'#' + (discountForm.invoice_no || '')"
              disabled
            />
          </b-form-group>

          <b-form-group
            :label="$t('common.current-amount')"
            label-for="discount-current-amount"
          >
            <b-form-input
              id="discount-current-amount"
              :value="formatIQDCurrency(discountForm.amount)"
              disabled
            />
          </b-form-group>

          <b-form-group
            :label="$t('common.discount-amount')"
            label-for="discount-amount"
            :invalid-feedback="discountError"
            :state="discountError ? false : null"
          >
            <b-form-input
              id="discount-amount"
              v-model="discountForm.discount"
              type="number"
              min="0"
              :placeholder="$t('common.enter-discount-amount')"
              :state="discountError ? false : null"
            />
          </b-form-group>

          <b-alert
            v-if="discountForm.discount > 0"
            variant="info"
            show
            class="mt-2"
          >
            <div class="d-flex justify-content-between">
              <span>{{ $t("common.amount-after-discount") }}:</span>
              <strong>{{
                formatIQDCurrency(calculateAmountAfterDiscount())
              }}</strong>
            </div>
          </b-alert>
        </b-card-text>
      </b-modal>
    </b-col>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BModal,
  BFormInput,
  BButton,
  BLink,
  BDropdown,
  BSpinner,
  BDropdownItem,
  BTooltip,
  BFormGroup,
  BFormSelect,
  BCardText,
  BFormCheckboxGroup,
  BFormCheckbox,
  BCardBody,
  BTable,
  BBadge,
  BFormDatepicker,
  BCollapse,
  BCardHeader,
  BAlert,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import Datepicker from 'vuejs-datepicker'
import { getNestedProperty } from '@/utils/nestedProperty.js'
import { Datetime } from 'vue-datetime'
import 'vue-datetime/dist/vue-datetime.css'
import ActionsButtons from '@/components/common/ActionsButtons.vue'
import DeleteModal from '@/components/modals/DeleteModal.vue'
import router from '@/router'

const AutoComplete = () => import('@/components/common/AutoComplete.vue')
const ServicesInvoice = () => import('@/components/printables/ServicesInvoice.vue')
const ChangeInvoiceStatusDropDown = () => import('@/components/invoices/ChangeInvoiceStatusDropDown.vue')

export default {
  components: {
    BCard,
    BCardText,
    BRow,
    BBadge,
    BCol,
    BFormInput,
    BButton,
    BLink,
    BDropdown,
    BDropdownItem,
    BTooltip,
    vSelect,
    ToastificationContent,
    Datetime,
    DeleteModal,
    ActionsButtons,
    BFormGroup,
    BFormSelect,
    BSpinner,
    BModal,
    BCardText,
    BFormCheckbox,
    BFormCheckboxGroup,
    BCardBody,
    BTable,
    Datepicker,
    BFormDatepicker,
    BCollapse,
    BCardHeader,
    BAlert,
    ChangeInvoiceStatusDropDown,
    ServicesInvoice,
    AutoComplete,
  },
  props: [
    'invoices',
    'patientData',
    'patientSurgeryId',
    'getData',
    'patient_card_surgery_id',
  ],
  data() {
    return {
      tableColumns1: [
        {
          key: 'edit',
          label: this.$t('common.edit'),
        },
        {
          key: 'invoice_no',
          label: this.$t('surgery.invoice_no'),
          sortable: true,
        },
        {
          key: 'date',
          label: this.$t('common.date'),
          sortable: true,
        },
        {
          key: 'room_amount',
          label: this.$t('surgery.room_amount'),
          sortable: true,
        },
        {
          key: 'admin_services_amount',
          label: this.$t('surgery.admin_services_amount'),
          sortable: true,
        },
        {
          key: 'general_services_amount',
          label: this.$t('surgery.general_services_amount'),
          sortable: true,
        },
        {
          key: 'nursing_amount',
          label: this.$t('surgery.nursing_amount'),
          sortable: true,
        },
        {
          key: 'surgery_nurse_assist_amount',
          label: this.$t('surgery.surgery_nurse_assist_amount'),
          sortable: true,
        },
        {
          key: 'surgery_room_amount',
          label: this.$t('surgery.surgery_room_amount'),
          sortable: true,
        },
        {
          key: 'labour_room_amount',
          label: this.$t('surgery.labour_room_amount'),
          sortable: true,
        },
        {
          key: 'medical_consumables_amount',
          label: this.$t('surgery.medical_consumables_amount'),
          sortable: true,
        },
        {
          key: 'pre_incubator_amount',
          label: this.$t('surgery.pre_incubator_amount'),
          sortable: true,
        },

        {
          key: 'show_more',
          label: this.$t('common.show_more'),
        },
      ],

      tableColumns2: [
        {
          key: 'invoice_no',
          label: this.$t('surgery.invoice_no'),
          sortable: true,
        },

        {
          key: 'pre_cpap_amount',
          label: this.$t('surgery.pre_cpap_amount'),
          sortable: true,
        },
        {
          key: 'scanner_device_amount',
          label: this.$t('surgery.scanner_device_amount'),
          sortable: true,
        },
        {
          key: 'surgeon_amount',
          label: this.$t('surgery.surgeon_amount'),
          sortable: true,
        },
        {
          key: 'surgeon_assist_amount',
          label: this.$t('surgery.surgeon_assist_amount'),
          sortable: true,
        },
        {
          key: 'anesthetist_amount',
          label: this.$t('surgery.anesthetist_amount'),
          sortable: true,
        },
        {
          key: 'anaesthetist_assist_amount',
          label: this.$t('surgery.anaesthetist_assist_amount'),
          sortable: true,
        },
        {
          key: 'pre_test_amount',
          label: this.$t('surgery.pre_test_amount'),
          sortable: true,
        },
        {
          key: 'tissue_transplant_amount',
          label: this.$t('surgery.tissue_transplant_amount'),
          sortable: true,
        },
        {
          key: 'op_nurse_amount',
          label: this.$t('surgery.op_nurse_amount'),
          sortable: true,
        },
        {
          key: 'consultation_amount',
          label: this.$t('surgery.consultation_amount'),
          sortable: true,
        },
        {
          key: 'status',
          sortable: true,
          label: this.$t('common.status'),
        },
        { key: 'Actions', label: this.$t('common.actions') },
      ],

      invoiceLatestStatus: '',
      InvoiceId: '',
      invoice: {},
      ServicesInvoiceBtn: {
        id: 'ServicesInvoice',
      },
      EditForm: {
        invoice_insurance_surgery_id: '',
        patient_card_surgery_id: '',
        room_amount: '',
        admin_services_amount: '',
        general_services_amount: '',
        nursing_amount: '',
        surgery_nurse_assist_amount: '',
        surgery_room_amount: '',
        labour_room_amount: '',
        medical_consumables_amount: '',
        pre_incubator_amount: '',
        pre_cpap_amount: '',
        scanner_device_amount: '',
        surgeon_amount: '',
        surgeon_assist_amount: '',
        anesthetist_amount: '',
        anaesthetist_assist_amount: '',
        pre_test_amount: '',
        tissue_transplant_amount: '',
        op_nurse_amount: '',
        consultation_amount: '',
        date: '',
      },
      date: new Date().toLocaleString('en-US', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
        timeZone: 'Asia/Baghdad',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      }),

      errorForm: {
        invoice_insurance_surgery_id: '',
        patient_card_surgery_id: '',
        room_amount: '',
        admin_services_amount: '',
        general_services_amount: '',
        nursing_amount: '',
        surgery_nurse_assist_amount: '',
        surgery_room_amount: '',
        labour_room_amount: '',
        medical_consumables_amount: '',
        pre_incubator_amount: '',
        pre_cpap_amount: '',
        scanner_device_amount: '',
        surgeon_amount: '',
        surgeon_assist_amount: '',
        anesthetist_amount: '',
        anaesthetist_assist_amount: '',
        pre_test_amount: '',
        tissue_transplant_amount: '',
        op_nurse_amount: '',
        consultation_amount: '',
        date: '',
      },
      params: {
        form: {},
        path: '',
      },
      surgeryList: [],
      insuranceSurgeryList: [],
      loading: false,
      splitDummyData1: [],
      splitDummyData2: [],
      dateTo: '',
      dateFrom: '',
      sums: {
        hospital_net_amount_sum: 0,
        total_amount_sum: 0,
      },
      surgeryName: '',
      isEditing: false,
      discountForm: {
        id: null,
        invoice_no: '',
        amount: 0,
        discount: 0,
      },
      discountError: '',
      expandedInvoiceId: null,
    }
  },

  mounted() {
    const today = new Date()
    this.dateFrom = `${today.getFullYear()}-${String(
      today.getMonth() + 1,
    ).padStart(2, '0')}-01`
    this.getPatientSurgeriesInvoices()
    this.getPatientSurgeryInfo()
    // this.splitDummyData();
    console.log('date', this.date)
    console.log('patientData22', this.patientData)
  },
  methods: {
    getNestedProperty,

    formatIQDCurrency(value) {
      // console.log("value", value);
      // Create a new Intl.NumberFormat object for IQD currency
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'IQD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })

      // Format the value and return it
      return formatter.format(value)
    },
    formatDate(surgeryDate) {
      const date = new Date(surgeryDate)

      const year = date.getFullYear()
      let month = `${date.getMonth() + 1}`
      let day = `${date.getDate()}`
      let hours = `${date.getHours()}`
      let minutes = `${date.getMinutes()}`
      let seconds = `${date.getSeconds()}`

      if (month.length < 2) month = `0${month}`
      if (day.length < 2) day = `0${day}`
      if (hours.length < 2) hours = `0${hours}`
      if (minutes.length < 2) minutes = `0${minutes}`
      if (seconds.length < 2) seconds = `0${seconds}`

      // Return the formatted date and time string
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    },
    printInvoice() {
      this.$refs.surgeryInvoiceRef.click()
      this.changeStatusToPrinted()
    },
    async showModal(invoiceData) {
      const surgeryId = getNestedProperty(invoiceData, 'patient_card_surgery.surgery_id')

      if (surgeryId) {
        await this.getSurgeryName(surgeryId)
        this.invoice = invoiceData
        this.invoice.surgery_name = this.surgeryName
      } else {
        this.invoice = invoiceData
        this.invoice.surgery_name = 'N/A'
      }

      console.log('invoice', this.invoice)
      this.$bvModal.show('modal-confirm-print')
    },
    async getSurgeryName(surgeryId) {
      await this.getList(`/surgery/${surgeryId}`)
      this.surgeryName = this.all_data.title
    },
    async onInsuranceSurgeryChange(insuranceSurgeryId) {
      if (!insuranceSurgeryId) return

      try {
        // Find the selected insurance surgery to get surgery_id
        const selectedInsurance = this.insuranceSurgeryList.find(
          item => item.value === insuranceSurgeryId,
        )

        if (!selectedInsurance) return

        // Fetch patient_card_surgery_id using surgery_id
        const response = await this.$http.get(
          `/patient-surgery?filter[patient_card_id]=${this.$route.query.patientCardId}&filter[surgery_id]=${selectedInsurance.surgery_id}`,
        )

        if (response.data.data && response.data.data.length > 0) {
          this.EditForm.patient_card_surgery_id = response.data.data[0].id
        } else {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: this.$t('common.error'),
              variant: 'warning',
              text: 'Patient surgery record not found',
            },
          })
        }
      } catch (error) {
        console.error('Error fetching patient surgery:', error)
      }
    },

    openAddModal() {
      this.isEditing = false
      this.EditForm = {
        invoice_insurance_surgery_id: '',
        patient_card_surgery_id: '',
        room_amount: 0,
        admin_services_amount: 0,
        general_services_amount: 0,
        nursing_amount: 0,
        surgery_nurse_assist_amount: 0,
        labour_room_amount: 0,
        medical_consumables_amount: 0,
        pre_incubator_amount: 0,
        pre_cpap_amount: 0,
        scanner_device_amount: 0,
        surgery_room_amount: 0,
        surgeon_amount: 0,
        surgeon_assist_amount: 0,
        anesthetist_amount: 0,
        anaesthetist_assist_amount: 0,
        pre_test_amount: 0,
        tissue_transplant_amount: 0,
        op_nurse_amount: 0,
        consultation_amount: 0,
        pre_ventameter_amount: 0,
        management_services_amount: 0,
        delivery_room_amount: 0,
        date: '',
      }
      // Reset error messages
      for (const item in this.errorForm) {
        this.errorForm[item] = ''
      }
      this.$bvModal.show('add-surgery-invoice-modal')
    },
    changeStatusToPrinted() {
      console.log('changeStatusToPrinted');
      (this.params.form = { status: 'printed' }),
      (this.params.path = `/invoicePatientSergury/${this.invoice.id}/changeStatus`),
      this.$store
        .dispatch('put', this.params)
        .then(response => {
          this.getPatientSurgeriesInvoices()
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: `${this.$t('common.success')}`,
              variant: 'success',
              text: `${response.data.message}`,
            },
          })
        })
        .catch(error => {
          if (error.response.status === 400) {
            for (const item in error.response.data) {
              this.$toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                  title: 'validation error',
                  variant: 'danger',
                  text: error.response.data[item][0],
                },
              })
            }
          }
        })
    },
    // Format a date string from "2025-03-26 13:17:00 pm" to "2025-03-26 13:17:00"
    formatPmDate(dateString) {
      if (!dateString || typeof dateString !== 'string') {
        return ''
      }

      // Check if the date string contains 'am' or 'pm'
      if (
        dateString.toLowerCase().includes('am')
        || dateString.toLowerCase().includes('pm')
      ) {
        const parts = dateString.split(' ')
        if (parts.length >= 3) {
          const datePart = parts[0] // e.g., 2025-03-26
          const timePart = parts[1] // e.g., 13:17:00
          const ampm = parts[2].toLowerCase() // e.g., pm

          // Parse the time
          const [hours, minutes, seconds] = timePart.split(':').map(Number)

          // Convert to 24-hour format if needed
          let hour = hours
          if (ampm === 'pm' && hours < 12) {
            hour = hours + 12
          } else if (ampm === 'am' && hours === 12) {
            hour = 0
          }

          // Format the time with leading zeros
          const formattedHour = hour < 10 ? `0${hour}` : `${hour}`
          const formattedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`
          const formattedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`

          // Return the formatted date and time
          return `${datePart} ${formattedHour}:${formattedMinutes}:${formattedSeconds}`
        }
      }

      // If the format is not as expected, return the original
      return dateString
    },
    editInvoice(item) {
      this.isEditing = true
      this.EditForm = { ...item }
      // Format the date properly if it exists
      if (this.EditForm.date) {
        // Convert from format like "2025-03-26 13:17:00 pm" to "2025-03-26 13:17:00"
        this.EditForm.date = this.formatPmDate(this.EditForm.date)
        this.date = this.EditForm.date
      }
      // Reset error messages
      for (const item in this.errorForm) {
        this.errorForm[item] = ''
      }
      this.$bvModal.show('add-surgery-invoice-modal')
    },
    submitItem() {
      const count = 0
      this.loading = true
      this.EditForm.date = this.formatDate(this.date)
      this.EditForm.pre_ventameter_amount = 0
      this.EditForm.management_services_amount = 0
      this.EditForm.pre_ventameter_amount = 0
      this.EditForm.delivery_room_amount = 0

      // Only validate insurance surgery for CREATE mode
      if (!this.isEditing) {
        // Validate insurance surgery selection
        if (!this.EditForm.invoice_insurance_surgery_id) {
          this.errorForm.invoice_insurance_surgery_id = 'Please select an insurance surgery'
          this.loading = false
          return false
        }

        // Validate patient_card_surgery_id was fetched
        if (!this.EditForm.patient_card_surgery_id) {
          this.errorForm.invoice_insurance_surgery_id = 'Unable to find patient surgery record'
          this.loading = false
          return false
        }

        // Set related_invoice_id to link to the insurance invoice
        this.EditForm.related_invoice_id = this.EditForm.invoice_insurance_surgery_id

        // Remove the insurance surgery id as it's not needed in the surgery invoice
        delete this.EditForm.invoice_insurance_surgery_id
      }

      console.log('editForm', this.EditForm)
      if (count > 0) {
        console.log('errorForm', this.errorForm)
        console.log('count', count)
        this.loading = false
        return false
      }

      // Handle CREATE vs UPDATE
      if (this.isEditing) {
        // Update existing invoice - only send amount fields
        const invoiceId = this.EditForm.id

        const updatePayload = {
          room_amount: this.EditForm.room_amount,
          admin_services_amount: this.EditForm.admin_services_amount,
          general_services_amount: this.EditForm.general_services_amount,
          nursing_amount: this.EditForm.nursing_amount,
          surgery_nurse_assist_amount: this.EditForm.surgery_nurse_assist_amount,
          surgery_room_amount: this.EditForm.surgery_room_amount,
          labour_room_amount: this.EditForm.labour_room_amount,
          medical_consumables_amount: this.EditForm.medical_consumables_amount,
          pre_incubator_amount: this.EditForm.pre_incubator_amount,
          pre_cpap_amount: this.EditForm.pre_cpap_amount,
          scanner_device_amount: this.EditForm.scanner_device_amount,
          surgeon_amount: this.EditForm.surgeon_amount,
          surgeon_assist_amount: this.EditForm.surgeon_assist_amount,
          anesthetist_amount: this.EditForm.anesthetist_amount,
          anaesthetist_assist_amount: this.EditForm.anaesthetist_assist_amount,
          pre_test_amount: this.EditForm.pre_test_amount,
          tissue_transplant_amount: this.EditForm.tissue_transplant_amount,
          op_nurse_amount: this.EditForm.op_nurse_amount,
          consultation_amount: this.EditForm.consultation_amount,
          pre_ventameter_amount: this.EditForm.pre_ventameter_amount,
          management_services_amount: this.EditForm.management_services_amount,
          delivery_room_amount: this.EditForm.delivery_room_amount,
          date: this.EditForm.date,
          _method: 'PUT',
        };

        (this.params.form = updatePayload),
        (this.params.path = `invoicePatientSergury/${invoiceId}`),
        this.$store
          .dispatch('update', this.params)
          .then(async response => {
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: this.$t('common.success'),
                variant: 'success',
                text: this.$t('common.updated-successfully'),
              },
            })
            this.$bvModal.hide('add-surgery-invoice-modal')
            this.getPatientSurgeriesInvoices()
          })
          .catch(error => {
            if (error.response.status === 400) {
              for (const item in error.response.data) {
                this.$toast({
                  component: ToastificationContent,
                  position: 'top-right',
                  props: {
                    title: 'validation error',
                    variant: 'danger',
                    text: error.response.data[item][0],
                  },
                })
              }
            }
          })
          .finally(() => {
            this.loading = false
          })
      } else {
        // Create new invoice
        (this.params.form = this.EditForm),
        (this.params.path = 'invoicePatientSergury'),
        this.$store
          .dispatch('create', this.params)
          .then(async response => {
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: this.$t('common.success'),
                variant: 'success',
                text: this.$t('common.created-successfully'),
              },
            })
            this.$bvModal.hide('add-surgery-invoice-modal')
            this.getPatientSurgeriesInvoices()
          })
          .catch(error => {
            if (error.response.status === 400) {
              for (const item in error.response.data) {
                this.$toast({
                  component: ToastificationContent,
                  position: 'top-right',
                  props: {
                    title: 'validation error',
                    variant: 'danger',
                    text: error.response.data[item][0],
                  },
                })
              }
            }
          })
          .finally(() => {
            this.loading = false
          })
      }
    },
    async getPatientSurgeryInfo() {
      this.loading = true
      try {
        // Fetch insurance surgeries that don't have a surgery invoice yet
        const response = await this.$http.get(
          `/invoices?filter[patient_card_id]=${this.$route.query.patientCardId}&filter[type]=invoice_insurance_surgery&include=surgery,invoicePatientSurgery&take=1000&sortBy=-id`,
        )

        // Filter only insurance surgeries where invoice_patient_surgery is null
        const availableInsuranceSurgeries = response.data.data.filter(
          item => item.invoice_patient_surgery === null,
        )

        this.insuranceSurgeryList = availableInsuranceSurgeries.map(item => ({
          title: `#${item.invoice_no} - ${item.surgery.title}`,
          value: item.id,
          surgery_id: item.surgery_id,
        }))

        if (this.insuranceSurgeryList.length > 0) {
          this.EditForm.invoice_insurance_surgery_id = this.insuranceSurgeryList[0].value
          // Fetch patient_card_surgery_id for the first insurance surgery
          await this.onInsuranceSurgeryChange(this.insuranceSurgeryList[0].value)
        }
      } catch (error) {
        console.error('Error fetching insurance surgeries:', error)
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: this.$t('common.error-fetching-data'),
          },
        })
      } finally {
        this.loading = false
      }
    },
    async getPatientSurgeriesInvoices() {
      this.loading = true
      const url = `/invoicePatientSergury?include=patientCardSurgery,relatedInvoice&patient_id=${this.$route.params.patientId}&take=10000&sort=-id`

      await this.getList(url)
      this.sums.hospital_net_amount_sum = this.all_data.sums.hospital_net_amount_sum
      this.sums.total_amount_sum = this.all_data.sums.total_amount_sum
      this.splitDummyData1 = this.all_data.data
      this.splitDummyData2 = this.all_data.data
      console.log('invoices11111', this.splitDummyData1)
      this.loading = false
    },
    // splitDummyData() {
    //   setTimeout(() => {
    //     // console.log("Original dummyData:", this.dummyData);
    //     // this.splitDummyData1 = [...this.dummyData].map((item) => {
    //     //   let data = {};
    //     //   this.tableColumns1.forEach((column) => {
    //     //     data[column.key] = item[column.key];
    //     //   });
    //     //   return data;
    //     // });
    //     // this.splitDummyData1 = this.dummyData;
    //     this.splitDummyData1 = this.invoices;
    //     this.dataCount = this.invoices.length;

    //     // console.log("Processed splitDummyData1:", this.splitDummyData1);

    //     // this.splitDummyData2 = [...this.dummyData].map((item) => {
    //     //   let data = {};
    //     //   this.tableColumns2.forEach((column) => {
    //     //     data[column.key] = item[column.key];
    //     //   });
    //     //   return data;
    //     // });
    //     // this.splitDummyData2 = this.dummyData;
    //     // this.dataCount = this.dummyData.length;
    //     this.splitDummyData2 = this.invoices;
    //     this.dataCount = this.invoices.length;
    //     // console.log("Processed splitDummyData2:", this.splitDummyData2);
    //   }, 1000);
    // },

    // Toggle collapse for invoice details
    toggleCollapse(invoiceId) {
      this.$root.$emit('bv::toggle::collapse', `accordion-${invoiceId}`)
    },

    // Show discount modal
    showDiscountModal(invoice) {
      this.discountForm = {
        id: invoice.id,
        invoice_no: invoice.invoice_no || '',
        amount: parseFloat(invoice.total_amount) || 0,
        discount: parseFloat(invoice.discount) || 0,
      }
      this.discountError = ''
      this.$bvModal.show('modal-apply-discount')
    },

    // Calculate amount after discount
    calculateAmountAfterDiscount() {
      const amount = parseFloat(this.discountForm.amount) || 0
      const discount = parseFloat(this.discountForm.discount) || 0
      return Math.max(0, amount - discount)
    },

    // Apply discount
    async applyDiscount(bvModalEvt) {
      bvModalEvt.preventDefault()

      // Validate discount
      const discount = parseFloat(this.discountForm.discount)
      const amount = parseFloat(this.discountForm.amount)

      if (isNaN(discount) || discount < 0) {
        this.discountError = this.$t('common.invalid-discount')
        return
      }

      if (discount > amount) {
        this.discountError = this.$t('common.discount-exceeds-amount')
        return
      }

      try {
        this.loading = true

        const payload = {
          discount,
        }

        await this.$http.put(
          `/invoices/${this.discountForm.id}`,
          payload,
        )

        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.success'),
            variant: 'success',
            text: this.$t('common.discount-applied-successfully'),
          },
        })

        this.$bvModal.hide('modal-apply-discount')

        // Refresh data
        this.getPatientSurgeriesInvoices()
      } catch (error) {
        console.error('Error applying discount:', error)

        // Handle validation errors (400)
        if (error.response && error.response.status === 400) {
          const errors = error.response.data

          // Check if discount field has errors
          if (errors.discount && Array.isArray(errors.discount)) {
            this.discountError = errors.discount[0]
          } else {
            this.discountError = this.$t('common.discount-apply-error')
          }
        } else {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: this.$t('common.error'),
              variant: 'danger',
              text: this.$t('common.discount-apply-error'),
            },
          })
        }
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.accordion .badge-light {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  transition: background-color 0.3s ease, box-shadow 0.3s ease, border-radius 0.3s ease;
  border-radius: 0.5rem;
}

.accordion .badge-light:hover {
  background-color: #e9ecef;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* When badge is expanded, remove bottom border radius and border */
.accordion .badge-light.badge-expanded {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  border-bottom: none;
}

/* Collapse content styling - seamlessly connects to badge */
.collapse-content {
  background-color: #ffffff;
  border: 1px solid #dee2e6;
  border-top: none;
  border-bottom-left-radius: 0.5rem;
  border-bottom-right-radius: 0.5rem;
  padding: 1.5rem;
  margin-top: 0;
}

.hidden {
  display: none;
}

/* Smooth collapse animation */
.collapse {
  transition: height 0.35s ease;
}

/* Chevron icon rotation */
.collapse-icon {
  transition: transform 0.3s ease;
  color: #7367f0;
}

.collapse-icon.rotate-90 {
  transform: rotate(90deg);
}

/* Full width badge */
.w-100 {
  width: 100% !important;
}

/* Empty state styling */
.py-5 {
  padding-top: 3rem !important;
  padding-bottom: 3rem !important;
}

.text-center {
  text-align: center;
}

.mb-3 {
  margin-bottom: 1rem !important;
}

.mt-2 {
  margin-top: 0.5rem !important;
}
</style>
